/*
 * Dinowax accordions — smooth open and close on native <details>.
 *
 * WHY THIS NEEDS JAVASCRIPT AT ALL.
 * A <details> element has no animatable state. The browser shows and hides its
 * content the instant `open` flips, and there is no transition to hook. CSS is
 * close to a fix — ::details-content plus interpolate-size: allow-keywords
 * animates to a content-driven height — but interpolate-size is still
 * Chromium-only, so it cannot be the only mechanism on a public storefront.
 * Revisit when Gecko and WebKit ship it; this file then collapses to a few
 * lines of CSS.
 *
 * WHY THE name ATTRIBUTE IS REMOVED.
 * The markup uses <details name="..."> so a group behaves as an exclusive
 * accordion with no script. That grouping is enforced on the `open` SETTER, not
 * on the click: the moment one panel gains `open`, every sibling sharing the
 * name loses it, synchronously.
 *
 * An animated close needs the opposite — the panel being closed has to KEEP
 * `open` for the length of its animation, because losing it hides the content
 * immediately and there is nothing left to animate. Those two requirements
 * cannot both hold, so the usual workaround (re-open the sibling, measure it,
 * then animate it shut) actively backfires: setting open=true on the sibling
 * re-runs the exclusivity algorithm and slams shut the panel the user just
 * asked for.
 *
 * So the name is lifted off at init and remembered on data-dw-acc-group, and
 * this file owns exclusivity instead. The degradation is deliberate and good:
 * if this script never runs, the name attributes stay exactly as authored and
 * the accordions still work — natively, exclusively, just without animation.
 *
 * WHY A WRAPPER IS ANIMATED, NOT THE <details>.
 * The widely copied recipe animates the height of the <details> itself and
 * derives the target from summary.offsetHeight + content.offsetHeight. That
 * arithmetic is wrong the moment the summary carries padding, which it does
 * here. Animating a dimensionless wrapper instead means the target is one
 * getBoundingClientRect call and the <details> keeps its own padding, border
 * and radius throughout.
 *
 * ACCESSIBILITY.
 * Only `click` is intercepted. Enter and Space on a focused <summary> dispatch
 * a real click, so one listener covers pointer and keyboard alike, and adding a
 * keydown listener would toggle twice per key press.
 *
 * aria-expanded is deliberately never written. The browser maps it from the
 * open state natively, and hand-writing it would announce "collapsed" while the
 * panel is still visibly animating open — we hold `open` through the close, so
 * a hand-written value would contradict what is on screen.
 */
(function () {
  'use strict';

  if (window.__dwAccordionReady) return;   // both sections load this file
  window.__dwAccordionReady = true;

  var DURATION = 320;
  var EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var PANEL = '[data-dw-acc-panel]';

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function panelOf(d) {
    /* Only this element's own panel. A nested <details> would otherwise have
       its panel claimed by the ancestor and animate to the wrong height. */
    var found = null;
    each(d.children, function (child) {
      if (!found && child.hasAttribute && child.hasAttribute('data-dw-acc-panel')) found = child;
    });
    return found;
  }

  /* Cancel any run in flight so a fast second click reverses from where the
     panel actually is, rather than fighting the previous animation. */
  function stop(d) {
    if (d.__dwAnim) {
      try { d.__dwAnim.cancel(); } catch (e) { /* already gone */ }
      d.__dwAnim = null;
    }
  }

  function height(el) {
    return el.getBoundingClientRect().height;
  }

  function animate(d, panel, from, to, done) {
    stop(d);
    if (reduced() || !panel.animate) { done(); return; }

    panel.style.overflow = 'hidden';
    var run = panel.animate(
      [{ height: from + 'px' }, { height: to + 'px' }],
      { duration: DURATION, easing: EASING }
    );
    d.__dwAnim = run;

    /*
     * Both handlers check identity before clearing, and that guard is load
     * bearing: WAAPI queues the cancel event to the next animation step rather
     * than firing it inside .cancel(), so it lands about a frame LATE — by
     * which time __dwAnim usually holds the replacement animation.
     *
     * Without the guard, a third click inside the 320ms window stranded the
     * whole accordion shut. Closing cancelled the open animation and stored the
     * close; a frame later the cancelled animation's handler nulled __dwAnim,
     * discarding the close that was still running; the next click's stop() then
     * found nothing to cancel, measured its target against a panel that was
     * still shrinking, and animated from a height to the same height. The
     * orphaned close finished last and removed `open` — so the panel the
     * shopper had just asked for ended up closed, along with its siblings.
     */
    run.onfinish = function () {
      if (d.__dwAnim === run) d.__dwAnim = null;
      done();
      panel.style.overflow = '';
    };
    run.oncancel = function () {
      if (d.__dwAnim === run) d.__dwAnim = null;
    };
  }

  /*
   * INTENT, NOT THE ATTRIBUTE.
   * `open` is deliberately held true for the whole closing animation, so the
   * attribute cannot answer "is this panel open?" while one is running — and
   * reading it directly is a real bug, not a theoretical one: clicking a panel
   * twice quickly made the second click read "still open" and start a second
   * close, so the panel ended up shut when the user had just asked for it back.
   */
  function isOpen(d) {
    return d.__dwClosing ? false : d.hasAttribute('open');
  }

  /*
   * Announced synchronously, the moment the shopper acts.
   *
   * The native `toggle` event cannot serve this. It fires when `open` actually
   * changes, and a close deliberately holds `open` for the length of its
   * animation — so anything mirroring the open state (the cross-sell buy bar,
   * for one) would sit stale for 320ms on every deselect and read as broken.
   */
  function announceIntent(d, open) {
    if (typeof window.CustomEvent !== 'function') return;
    d.dispatchEvent(new CustomEvent('dw:accordion:intent', {
      bubbles: true,
      detail: { open: open }
    }));
  }

  function openOne(d) {
    var panel = panelOf(d);
    if (!panel) { d.__dwClosing = false; d.setAttribute('open', ''); return; }

    // Measured BEFORE stop(), because reversing a close mid-flight has to start
    // from the height on screen rather than from zero.
    var from = d.hasAttribute('open') ? height(panel) : 0;
    stop(d);
    d.__dwClosing = false;

    closeGroup(d);
    // `open` before measuring the target: the content is display:none until it
    // is set, so it has no height before this line.
    d.setAttribute('open', '');
    announceIntent(d, true);
    animate(d, panel, from, height(panel), function () {});
  }

  function closeOne(d) {
    var panel = panelOf(d);
    if (!panel) { d.removeAttribute('open'); return; }

    var from = height(panel);   // may be mid-open; start from what is on screen
    stop(d);
    d.__dwClosing = true;
    announceIntent(d, false);

    animate(d, panel, from, 0, function () {
      // Pin the collapsed height before dropping `open`, so the frame between
      // the animation ending and the content being hidden cannot paint the
      // panel back at its full height.
      panel.style.height = '0px';
      d.removeAttribute('open');
      panel.style.height = '';
      d.__dwClosing = false;
    });
  }

  function closeGroup(d) {
    var g = d.getAttribute('data-dw-acc-group');
    if (!g) return;
    each(document.querySelectorAll('[data-dw-acc-group="' + g + '"]'), function (other) {
      if (other !== d && isOpen(other)) closeOne(other);
    });
  }

  function setup(d) {
    if (d.__dwAcc) return;
    d.__dwAcc = true;

    var name = d.getAttribute('name');
    if (name) {
      d.setAttribute('data-dw-acc-group', name);
      d.removeAttribute('name');
    }

    var summary = d.querySelector('summary');
    if (!summary || summary.parentNode !== d) return;

    summary.addEventListener('click', function (e) {
      if (!panelOf(d)) return;              // no wrapper: leave native behaviour
      e.preventDefault();
      if (isOpen(d)) closeOne(d); else openOne(d);
    });
  }

  function init(root) {
    each((root || document).querySelectorAll('details[data-dw-acc]'), setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // The theme editor swaps a section's markup wholesale, and the replacement
  // arrives unbound.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
